import { ASSISTANT_TONES, type AssistantTone } from '@contracts/assistant';

const PREFIX = '[tone:';
/** The longest `[tone:<word>]` still waited for; a longer unterminated start is plain text. */
export const TONE_LABEL_MAX = 32;
const TONES: ReadonlySet<string> = new Set(ASSISTANT_TONES);

export type ToneParser = {
  /** Feeds a chunk; returns the text that is now safe to show (never any part of a leading label). */
  push(chunk: string): string;
  /** The segment ended: returns whatever was held (an unfinished label start is plain text). */
  end(): string;
  tone(): AssistantTone | null;
  /** A new segment (a new model attempt, D60): the next leading label is looked for again. */
  reset(): void;
};

/**
 * Spec §6.4: a leading `[tone:x]` is held until it is complete, even when a token boundary cuts it, so it never
 * reaches the screen. Only the eight listed tones count; another word is dropped silently (D62).
 */
export function createToneParser(): ToneParser {
  let held = '';
  let deciding = true;
  let tone: AssistantTone | null = null;
  let dropSpace = false;           // the label closed its chunk: the one space after it may arrive in the next push
  const release = (): string => { deciding = false; const out = held; held = ''; return out; };
  const settle = (): string => {
    const body = held.replace(/^\s+/, '');
    if (body === '') return '';
    if (body.length < PREFIX.length) {
      if (PREFIX.startsWith(body)) return '';         // '[to' — could still become a label
      return release();
    }
    if (!body.startsWith(PREFIX)) return release();
    const close = body.indexOf(']');
    if (close === -1) return body.length > TONE_LABEL_MAX ? release() : '';
    const word = body.slice(PREFIX.length, close);
    if (TONES.has(word)) tone = word as AssistantTone;
    deciding = false;
    held = '';
    const rest = body.slice(close + 1);
    if (rest === '') { dropSpace = true; return ''; }
    return rest.replace(/^ /, '');
  };
  return {
    push(chunk) {
      if (!deciding) {
        if (dropSpace && chunk !== '') { dropSpace = false; return chunk.replace(/^ /, ''); }
        return chunk;
      }
      held += chunk;
      return settle();
    },
    end() { const out = deciding ? held : ''; held = ''; deciding = false; dropSpace = false; return out; },
    tone: () => tone,
    reset() { held = ''; deciding = true; dropSpace = false; },
  };
}

/** The whole-text form the server stores (D61, D62). Equal to push-all + end for every chunking. */
export function stripTone(text: string): { text: string; tone: AssistantTone | null } {
  const t = createToneParser();
  const out = t.push(text) + t.end();
  return { text: out, tone: t.tone() };
}
